import moment from "moment";
import * as _ from "lodash";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import {
	Text,
	Button,
	useToast,
	Divider,
	useDisclosure,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
} from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import { useJob, useUpdateShifts } from "@api/hooks";
import { ScheduleWeekItem, ShiftItem } from "@models/Order";
import { useCallback, useEffect, useMemo, useState } from "react";
import LoadingIndicator from "../components/Loading";
import LeftArrow from "../components/icons/LeftArrow";
import {
	applyCopiedScheduleForSelectedWeek,
	getISOWeekString,
	getSwedishWeekDayName,
	getWeekItems,
	getWeekItemsWithShiftData,
	isValidTime,
} from "@utils/dateUtils";
import WeekItem from "../components/createOrder/WeekItem";
import EmptyWhiteCircle from "../components/icons/EmptyWhiteCircle";
import CheckCircleIcon from "../components/icons/CheckCircleIcon";
import { getShiftsWithStatus, removeUndefinedProperties } from "@utils/utils";

const ShiftEditPage = () => {
	const navigate = useNavigate();
	const params = useParams();
	const [searchParams] = useSearchParams();
	const services = searchParams.get("services") || "";
	const jobId = params.id || "";
	const { job, isLoading } = useJob(jobId);
	const { mutate: updateShiftsMutation, isLoading: isLoadingUpdateShifts } = useUpdateShifts();
	const toast = useToast();

	const [weekList, setWeekList] = useState<ScheduleWeekItem[]>([]);
	const [scheduleCopiedWeekNumberList, setScheduleCopiedWeekNumberList] = useState<string[]>([]);
	const [copiedShiftItems, setCopiedShiftItems] = useState<ShiftItem[]>([]);
	const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
	const [pastingDisabledWeekNumbers, setPastingDisabledWeekNumbers] = useState<string[]>([]);

	const orderNumber = job?.orderNumber ?? "";
	const customerName = job?.customer.name ?? "";
	const isLoadingData = isLoading || isLoadingUpdateShifts;

	const hasChangedShiftData = useMemo((): boolean => {
		if (!job) return false;
		const updatedShiftList = removeUndefinedProperties(
			weekList
				.flatMap((weekItem) => weekItem.dateList)
				.filter((item) => isValidTime(item.start) && isValidTime(item.start))
				.map(({ id, start, end, flexible, break: breakVal, hasWorked }) => ({
					id,
					start,
					end,
					flexible,
					hasWorked,
					break: breakVal,
				})),
		);

		if (updatedShiftList.length !== job.shifts?.length) return true;
		if (!_.isEqual(updatedShiftList, job?.shifts)) {
			return true;
		}
		return false;
	}, [weekList, job?.shifts]);

	// TODO : need to know overlapping schedules for pasting weeks
	// if copied-week-shifts has least one shift which will overlap with the pasting-week-shifts(which are booked), then it should be disabled to paste the shifts

	useEffect(() => {
		if (copiedShiftItems.length > 0) {
			const weekDaysOfCopiedWeek = copiedShiftItems
				.filter((csi) => isValidTime(csi.start))
				.map((csi) => moment(csi.start).day());
			const disableWeekNumbers: string[] = [];

			weekList.forEach((wi) => {
				wi.dateList.forEach((di) => {
					if (weekDaysOfCopiedWeek.includes(moment(di.start).day()) && di.tempStatus === "BOOKED_WITH_CONSULTANT")
						disableWeekNumbers.push(wi.weekNumber);
				});
			});
			setPastingDisabledWeekNumbers(disableWeekNumbers);
		}
	}, [copiedShiftItems]);

	useEffect(() => {
		if (job && job.shifts && job.shifts.length > 0 && job.applications) {
			setWeekList(
				getWeekItemsWithShiftData(
					getShiftsWithStatus(job.shifts, job.applications),
					26,
					moment.min(job.shifts.map((s) => moment(s.start))).format("YYYY-MM-DD"),
				),
			);
		} else {
			setWeekList(getWeekItems(26));
		}
	}, [job]);

	useEffect(() => {
		const weekListData = weekList.flatMap((weekItem) => weekItem.dateList);
		if (!!weekListData.find((item) => isValidTime(item.start) && isValidTime(item.start) && !item.id)) {
			setIsSaveButtonDisabled(false);
		} else {
			setIsSaveButtonDisabled(true);
		}
	}, [weekList]);

	const isCopyWeekButtonDisabled = scheduleCopiedWeekNumberList.length === 0;
	const {
		isOpen: isOpenCopyWeekDrawer,
		onOpen: onOpenCopyWeekDrawer,
		onClose: onCloseCopyWeekDrawer,
	} = useDisclosure();

	const handleChangeSchedule = useCallback(
		(dateItems: ShiftItem[], weekNumber: string) => {
			setWeekList((prev) => {
				const weekListData = [...prev];

				const toUpdateWeekItemIndex = prev.findIndex((wi) => wi.weekNumber === weekNumber);

				weekListData.splice(toUpdateWeekItemIndex, 1, {
					dateList: dateItems,
					weekNumber,
				});

				return weekListData;
			});
		},
		[weekList],
	);

	const handleCopyWeekButtonClick = useCallback(
		(copiedDateItems: ShiftItem[]) => {
			setCopiedShiftItems(copiedDateItems.filter((di) => isValidTime(di.start) && isValidTime(di.end)));
			onOpenCopyWeekDrawer();
		},
		[copiedShiftItems],
	);

	const handleConfirmCopyWeekSchedule = useCallback(() => {
		setWeekList((prev) => {
			const updatedWeekList = [...prev];
			scheduleCopiedWeekNumberList.forEach((copiedWeekNumber) => {
				const weekItemIndex = updatedWeekList.findIndex((wi) => wi.weekNumber === copiedWeekNumber);

				copiedShiftItems.forEach((copiedScheduleItem) => {
					const dateIndex = updatedWeekList[weekItemIndex].dateList.findIndex(
						(di) => getSwedishWeekDayName(di.start) === getSwedishWeekDayName(copiedScheduleItem.start),
					);

					if (dateIndex >= 0) {
						updatedWeekList[weekItemIndex].dateList.splice(
							dateIndex,
							1,
							applyCopiedScheduleForSelectedWeek(copiedWeekNumber, copiedScheduleItem),
						);
					}
				});
			});
			return updatedWeekList;
		});
		onCloseCopyWeekDrawer();
	}, [weekList, scheduleCopiedWeekNumberList]);

	const weekItemClickHandler = (weekNumber: string) => {
		setScheduleCopiedWeekNumberList((prev) => {
			if (prev.includes(weekNumber)) return prev.filter((wn) => wn !== weekNumber);
			return [...prev, weekNumber];
		});
	};

	const handleUpdateShifts = () => {
		const shiftList = weekList
			.flatMap((weekItem) => weekItem.dateList)
			.filter((item) => isValidTime(item.start) && isValidTime(item.start));

		updateShiftsMutation(
			{ jobId, shifts: shiftList },
			{
				onSuccess: () => {
					toast({
						title: "Förfrågan skickad",
						description: "Dina nya pass har skickats till konsulten.",
						status: "success",
						duration: 6000,
						isClosable: true,
						variant: "subtle",
					});
					navigate(-1);
				},
				onError: (err) => {
					toast({
						title: "Det gick inte att skicka förfrågan",
						description: "Försök igen om en stund.",
						status: "error",
						duration: 9000,
						isClosable: true,
						variant: "subtle",
					});
					console.error(err);
				},
			},
		);
	};

	return (
		<LoggedInLayout width="l" bg={"#edeff3"}>
			<Styles>
				<HStack justifyContent="space-between">
					<VStack alignItems="start">
						<Text fontWeight={600} fontSize={24} mt={2} mb={0}>
							Förläng uppdrag
						</Text>
						{!isLoadingData && orderNumber && customerName && services && (
							<Text fontWeight={400} fontSize={14} color={"#35353580"} my={0}>
								{`#${orderNumber}: ${customerName}, ${services}`}
							</Text>
						)}
						{isLoadingData && <LoadingIndicator size={"s"} />}
					</VStack>
					<Button
						variant="solid"
						height="40px"
						borderRadius={"20px"}
						width={"145px"}
						color="white"
						bg={"#2A2A2A"}
						_hover={{
							backgroundColor: isSaveButtonDisabled ? "#9fa0a3" : "#2A2A2A",
						}}
						onClick={handleUpdateShifts}
						isDisabled={!hasChangedShiftData}
						isLoading={isLoadingData}
					>
						Sänd förfrågan
					</Button>
				</HStack>
				<Divider height={"2px"} borderBottom={"2px solid #c8cacc"} my={4} />
				<Button onClick={() => navigate(-1)} leftIcon={<LeftArrow />} variant="ghost">
					Tillbaka
				</Button>

				<Box borderRadius={"lg"} backgroundColor={"white"} p={7} mt={3} mb={3}>
					{!isLoadingData &&
						weekList.map((weekItem) => (
							<WeekItem
								key={weekItem.weekNumber}
								dateList={weekItem.dateList}
								onChangeDateList={handleChangeSchedule}
								onCopyWeek={handleCopyWeekButtonClick}
								viewType="EDIT"
							/>
						))}
					{isLoadingData && <LoadingIndicator size={"s"} />}
					<Drawer
						placement={"right"}
						onClose={() => {
							onCloseCopyWeekDrawer();
							setScheduleCopiedWeekNumberList([]);
						}}
						isOpen={isOpenCopyWeekDrawer}
					>
						<DrawerOverlay />
						<DrawerContent borderLeftRadius={"25px"}>
							<DrawerCloseButton />
							<DrawerHeader borderBottomWidth="1px" textAlign={"center"}>
								<Text>Kopiera vecka</Text>
								<Text fontWeight={400} fontSize={13} color={"#999999"}>
									Du kan kopiera pass för vald vecka till en eller flera andra veckor.
								</Text>
							</DrawerHeader>
							<DrawerBody overflowX={"hidden"}>
								{weekList.map((wi) => {
									const shouldDisablePastingSchedule =
										(copiedShiftItems[0]?.start &&
											getISOWeekString(moment(copiedShiftItems[0]?.start).toDate()) === wi.weekNumber) ||
										moment().isAfter(wi.dateList[0].end, "day") ||
										pastingDisabledWeekNumbers.includes(wi.weekNumber);
									return (
										<Box
											key={wi.weekNumber}
											w="100%"
											my={"auto"}
											p={3}
											m={2}
											borderRadius={"18px"}
											border={"1px solid #E6E6E6"}
										>
											<HStack
												alignItems={"center"}
												cursor={shouldDisablePastingSchedule ? "not-allowed" : "pointer"}
												onClick={shouldDisablePastingSchedule ? undefined : () => weekItemClickHandler(wi.weekNumber)}
											>
												{scheduleCopiedWeekNumberList.includes(wi.weekNumber) || shouldDisablePastingSchedule ? (
													<CheckCircleIcon fillColor={shouldDisablePastingSchedule ? "#9EA0A6" : ""} />
												) : (
													<EmptyWhiteCircle />
												)}
												<Text fontWeight={500} fontSize={14} my={1} color={"#999999"}>
													{`Vecka ${wi.weekNumber.split("W")[1]}`}
												</Text>
											</HStack>
										</Box>
									);
								})}
							</DrawerBody>
							<Button
								variant="solid"
								height="40px"
								borderRadius={"20px"}
								color="white"
								bg={"#2A2A2A"}
								mx={"auto"}
								_hover={{
									backgroundColor: isCopyWeekButtonDisabled ? "#9fa0a3" : "#2A2A2A",
								}}
								onClick={handleConfirmCopyWeekSchedule}
								isDisabled={isCopyWeekButtonDisabled}
							>
								Kopiera skift till valda veckor
							</Button>
							<DrawerFooter></DrawerFooter>
						</DrawerContent>
					</Drawer>
				</Box>
			</Styles>
		</LoggedInLayout>
	);
};

export default ShiftEditPage;

const Styles = styled.div`
	width: 100%;
	padding-bottom: 25px;
`;
